import { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Loader2, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ForgotPassword() {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', null, { params: { email: email.trim() } });
      setSent(true);
    } catch (e) {
      toast.error(t('forgotPassword.error') || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-wp-darker flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="glass-card rounded-2xl p-8 space-y-6">
          {!sent ? (
            <>
              <div className="text-center space-y-2">
                <div className="w-14 h-14 rounded-2xl bg-wp-medium/20 flex items-center justify-center mx-auto">
                  <Mail className="h-7 w-7 text-wp-light" />
                </div>
                <h1 className="text-2xl font-bold text-white font-heading">
                  {t('forgotPassword.title') || 'Forgot Password'}
                </h1>
                <p className="text-wp-light/50 text-sm">
                  {t('forgotPassword.subtitle') || 'Enter your email and we\'ll send you a reset link'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-wp-light/70">{t('common.email')}</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="email@example.com"
                    required
                    className="bg-wp-dark/30 border-wp-dark/50 text-white placeholder:text-white/30"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="w-full bg-wp-medium hover:bg-wp-light text-white h-11 font-medium"
                >
                  {loading
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : (t('forgotPassword.send') || 'Send Reset Link')}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center space-y-4">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 flex items-center justify-center mx-auto">
                <CheckCircle className="h-7 w-7 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-white">
                {t('forgotPassword.sentTitle') || 'Check your email'}
              </h2>
              <p className="text-wp-light/50 text-sm">
                {t('forgotPassword.sentBody') || 'If this email is registered, you will receive a reset link shortly.'}
              </p>
            </div>
          )}

          <Link to="/login" className="flex items-center justify-center gap-2 text-wp-light/50 hover:text-wp-light text-sm transition-colors">
            <ArrowLeft className="h-4 w-4" />
            {t('forgotPassword.backToLogin') || 'Back to login'}
          </Link>
        </div>
      </div>
    </div>
  );
}
